<template>
  <div v-if="loading" class="flex items-center justify-center h-full">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else>
    <div class="w-full mx-8 my-2">
      {{ t('mainOperations') }}
    </div>
    <div class="flex flex-col flex-wrap content-center m-3 md:flex-row lg:justify-start">
      <template v-if="$can('create', '/sales/orders')">
        <action-button
          v-if="registerIsOpen"
          :title="$t('views.sales.newSale')"
          isSuccess
          @click="newSale()"
        >
          <custom-icon>
            <i-new-sale />
          </custom-icon>
        </action-button>
      </template>

      <template v-if="$can('read', '/sales/returns')">
        <action-button
          v-if="registerIsOpen"
          :title="$t('views.sales.orders.returnOrder')"
          isDanger
          @click="$router.push($Route.SALES_ORDERS_RETURN_ORDERS)"
        >
          <custom-icon>
            <i-return-sale />
          </custom-icon>
        </action-button>
      </template>

      <template v-if="$can('read', '/sales/orders')">
        <action-button
          :title="$t('views.sales.orders.orders')"
          faIcon="receipt"
          @click="$router.push($Route.SALES_ORDERS)"
        >
        </action-button>
        <action-button
          :title="$t('views.sales.transactions.transactions')"
          faIcon="exchange-alt"
          @click="$router.push($Route.SALES_TRANSACTIONS)"
        >
        </action-button>
      </template>

      <template v-if="$can('read', '/sales/quotes')">
        <action-button
          :title="t('quotes')"
          iconify="hugeicons:invoice-01"
          @click="$router.push($Route.SALES_QUOTES)"
        >
        </action-button>
      </template>
    </div>

    <div class="w-full mx-8 my-2">
      {{ t('registerOperations') }}
    </div>
    <div class="flex flex-col flex-wrap content-center m-3 md:flex-row lg:justify-start">
      <template v-if="$can('create', '/company/register-actions')">
        <action-button
          v-if="!registerIsOpen"
          :title="$t('views.sales.registers.openRegister')"
          faIcon="cash-register"
          @click="$router.push($Route.SALES_REGISTERS_OPEN_REGISTER)"
        >
        </action-button>
        <action-button
          v-else
          :title="$t('views.sales.registers.closeRegister')"
          faIcon="cash-register"
          @click="$router.push($Route.SALES_REGISTERS_CLOSE_REGISTER)"
        >
        </action-button>
      </template>
      <action-button
        v-if="$can('create', '/sales/discountcodes')"
        :title="t('discountCodes')"
        faIcon="tags"
        @click="$router.push($Route.SALES_DISCOUNT_CODES)"
      >
      </action-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import ActionButton from '@/components/ActionButton.vue'
import CustomIcon from '@/components/CustomIcon.vue'
import INewSale from '@/components/icons/sales/INewSale.vue'
import IReturnSale from '@/components/icons/sales/IReturnSale.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'

import { Register, RegisterLog } from '@/models/company/Register'
import {Order} from "@/models/sales/Order";
import {handleNewSale} from "@/lib/utils";

export default defineComponent({
  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          mainOperations: 'Main Operations',
          registerOperations: 'Register Operations',
          quotes: 'Quotes',
          discountCodes: 'Discount Codes'
        },
        ar: {
          mainOperations: 'العمليات الاساسية',
          registerOperations: 'عمليات الخزينة',
          quotes: 'عروض الأسعار',
          discountCodes: 'شفرات الخصم'
        }
      }
    })

    return {
      t
    }
  },
  components: {
    ActionButton,
    CustomIcon,
    INewSale,
    IReturnSale,
    LoadingSpinner,
    AlertBadge
  },

  data() {
    return {
      error: {
        title: '',
        body: ''
      },
      loading: false
    }
  },

  computed: {
    registerIsOpen() {
      return this.$ctx.currentRegister.status === 'open'
    }
  },

  async beforeMount() {
    const loadRegisterStatus = async () => {
      try {
        this.loading = true
        const registerLog = await this.$http.get<RegisterLog>(
          `${Register.ENDPOINT}/last-status/${this.$ctx.currentRegister.id}`
        )

        this.$ctx.currentRegister.status = RegisterLog.from(registerLog).action
      } catch (error) {
        this.error.title = error.title
        this.error.body = error.body
      } finally {
        this.loading = false
      }
    }
    // check if the register is open or not
    loadRegisterStatus()
  },
  methods: {
    canWithReason(): boolean {
      return this.$can('read', 'magazine')
    },
    newSale() {
      handleNewSale(this.t);
      this.$router.push(this.$Route.SALES_NEW_SALE);
    }
  }
})
</script>

<style></style>
